/**
 * @fileoverview EditCaregiverModal Component - Caregiver Information Editor
 * 
 * This component provides a modal dialog for editing caregiver/staff information in the
 * senior care facility management system. It includes form validation, real-time
 * error handling, and API integration for updating caregiver records.
 * 
 * Features:
 * - Comprehensive form with personal and professional information fields
 * - Real-time form validation with error messages
 * - Email format validation
 * - Responsive design for mobile and desktop
 * - Async submission with loading states
 * - Dropdown selectors for standardized fields (role, shift, status)
 * - Accessible form controls with proper labels
 * 
 * Form Fields:
 * - Personal: First Name*, Last Name*, Email*, Phone
 * - Professional: Role*, Primary Shift*, Badge ID, Employee ID, Status
 * (* = required fields)
 * 
 * @exports EditCaregiverModal - Main modal component
 * @exports FormField - Reusable form field wrapper (internal)
 * @exports TextInput - Styled text input component (internal)
 * @exports SelectInput - Styled select dropdown component (internal)
 */

import { useState, useEffect } from "react";
import apiService from "../services/api";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */

/**
 * Color Constants
 * Defines the color palette used throughout the modal component.
 */

/** @constant {string} W - White color for text and borders */
const W = "#ffffff";

/** @constant {string} CARD_BG - Dark blue background for the modal card */
const CARD_BG = "#042558";

/** @constant {string} INPUT_BG - Semi-transparent white background for input fields */
const INPUT_BG = "rgba(255,255,255,0.08)";

/** @constant {string} BORDER - Semi-transparent white border color */
const BORDER = "rgba(255,255,255,0.3)";

/** @constant {string} ACCENT - Blue accent color for headings and highlights */
const ACCENT = "#4A90E2";

/**
 * Form Options Constants
 * Predefined options for dropdown selectors to ensure data consistency.
 */

/** @constant {string[]} ROLE_OPTIONS - Available caregiver role types */
const ROLE_OPTIONS = ["RN", "LPN", "CNA", "PT", "NP", "ADMIN"];

/** @constant {string[]} SHIFT_OPTIONS - Available work shift options */
const SHIFT_OPTIONS = ["DAY", "EVENING", "NIGHT"];

/** @constant {string[]} STATUS_OPTIONS - Available caregiver status values */
const STATUS_OPTIONS = ["ACTIVE", "INACTIVE", "ON_LEAVE"];

/** @constant {string[]} CERT_TYPE_OPTIONS - Available certification types */
const CERT_TYPE_OPTIONS = [
  "BLS",
  "ACLS",
  "CPI",
  "FALL_PREVENTION",
  "DEMENTIA_CARE",
  "RN_LICENSE",
  "LPN_LICENSE",
  "CNA_LICENSE",
  "PT_LICENSE",
  "NP_LICENSE",
  "OTHER",
];

/* ═══════════════════════ FORM FIELD COMPONENTS ═══════════════════════ */

/**
 * FormField - Reusable Form Field Wrapper
 * 
 * Wraps form inputs with a consistent label and layout. Displays a red asterisk
 * for required fields and adapts font sizes based on mobile/desktop viewport.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Label text to display above the input
 * @param {React.ReactNode} props.children - Input component to render
 * @param {boolean} [props.required=false] - Whether to show required indicator (*)
 * @param {boolean} [props.isMobile=false] - Whether to use mobile-optimized styling
 * @returns {JSX.Element} Form field with label and input
 */
function FormField({ label, children, required, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          color: W,
          opacity: 0.85,
        }}
      >
        {label} {required && <span style={{ color: "#FF6B6B" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * TextInput - Styled Text Input Component
 * 
 * A styled text input field with focus states, consistent styling, and support
 * for different input types (text, email, tel, etc.). Includes hover and focus effects.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Callback function when value changes (receives new value)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.type="text"] - HTML input type (text, email, tel, etc.)
 * @param {Object} props.rest - Additional HTML input attributes
 * @returns {JSX.Element} Styled text input element
 */
function TextInput({ value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: W,
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        transition: "border-color 0.2s",
        width: "100%",
        boxSizing: "border-box",
      }}
      onFocus={(e) => (e.target.style.borderColor = ACCENT)}
      onBlur={(e) => (e.target.style.borderColor = BORDER)}
      {...rest}
    />
  );
}

/**
 * SelectInput - Styled Select Dropdown Component
 * 
 * A custom-styled select dropdown with consistent appearance across browsers.
 * Includes a custom dropdown arrow icon and placeholder support.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Callback function when selection changes (receives new value)
 * @param {string[]} props.options - Array of option values to display
 * @param {string} [props.placeholder] - Placeholder text for empty selection
 * @returns {JSX.Element} Styled select dropdown element
 */
function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: value ? W : "rgba(255,255,255,0.4)",
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='white' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 16px center",
      }}
    >
      <option value="" style={{ background: "#0e2240", color: W }}>
        {placeholder || "Select..."}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ background: "#0e2240", color: W }}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/* ═══════════════════════ MAIN EDIT MODAL ═══════════════════════ */

/**
 * EditCaregiverModal - Main Caregiver Editor Modal Component
 * 
 * A comprehensive modal dialog for editing caregiver/staff information. Handles form state,
 * validation, API submission, and provides user feedback through loading states and
 * error messages.
 * 
 * Workflow:
 * 1. Initializes form with existing caregiver data
 * 2. Validates user input on submission (including email format)
 * 3. Sends update request to API
 * 4. Notifies parent component of successful update
 * 5. Closes modal after successful submission
 * 
 * @param {Object} props - Component props
 * @param {Object} props.caregiver - Caregiver object containing current data
 * @param {string} props.caregiver.caregiver_id - Unique caregiver identifier
 * @param {string} props.caregiver.name - Full name with role (format: "FirstName LastName, Role")
 * @param {string} props.caregiver.email - Email address
 * @param {string} [props.caregiver.phone] - Phone number
 * @param {string} props.caregiver.role - Caregiver role (RN, LPN, CNA, etc.)
 * @param {string} props.caregiver.shift - Primary work shift
 * @param {string} [props.caregiver.badge_id] - Badge identification number
 * @param {string} [props.caregiver.employee_id] - Employee identification number
 * @param {string} [props.caregiver.status] - Employment status
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} [props.onUpdate] - Callback when caregiver is successfully updated (receives updated caregiver object)
 * @param {boolean} [props.isMobile=false] - Whether to use mobile-optimized layout
 * @returns {JSX.Element} Modal dialog with edit form
 */
export default function EditCaregiverModal({ caregiver, onClose, onUpdate, isMobile }) {
  // Debug: Log the caregiver object to see what we're receiving
  console.log('[EditCaregiverModal] Received caregiver object:', caregiver);
  console.log('[EditCaregiverModal] badge_id:', caregiver.badge_id);
  console.log('[EditCaregiverModal] employee_id:', caregiver.employee_id);
  
  /* ──────────────────────── Helper Functions ──────────────────────── */
  
  /**
   * Parses a full display name with role into first and last name components.
   * Handles the format "FirstName LastName, Role" and extracts just the name parts.
   * Also handles middle names by joining all parts after the first name.
   * 
   * @param {string} displayName - Full name with role (e.g., "Sarah Johnson, RN")
   * @returns {{firstName: string, lastName: string}} Object with parsed name parts
   */
  const parseNameFromDisplay = (displayName) => {
    if (!displayName) return { firstName: "", lastName: "" };
    
    // Remove the role part (everything after the comma)
    const nameWithoutRole = displayName.split(",")[0].trim();
    
    // Split into first and last name
    const nameParts = nameWithoutRole.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || ""; // Handle middle names
    
    return { firstName, lastName };
  };
  
    // Parse the caregiver's name into first and last name components
    const { firstName, lastName } = parseNameFromDisplay(caregiver.name);
  
    /* ──────────────────────── Load Certifications ──────────────────────── */
  
    /**
     * Load existing certifications when modal opens
     */
    useEffect(() => {
      const loadCertifications = async () => {
        try {
          setLoadingCerts(true);
          const response = await apiService.getCaregiverCertifications(caregiver.caregiver_id);
          setCertifications(response?.certifications || []);
        } catch (err) {
          console.error("Failed to load certifications:", err);
          setCertifications([]);
        } finally {
          setLoadingCerts(false);
        }
      };
    
      loadCertifications();
    }, [caregiver.caregiver_id]);

  /* ──────────────────────── Form State Management ──────────────────────── */
  
  /**
   * Form State Variables
   * Each state variable corresponds to a form field and is initialized
   * with the existing caregiver data.
   */
  
  // Personal Information
    const [formFirstName, setFormFirstName] = useState(firstName || "");
  const [formLastName, setFormLastName] = useState(lastName || "");
  const [email, setEmail] = useState(caregiver.email || "");
  const [phone, setPhone] = useState(caregiver.phone || "");
  
  // Professional Information
  const [role, setRole] = useState(caregiver.role || "");
  const [primaryShift, setPrimaryShift] = useState(caregiver.shift || "");
    const [badgeId, setBadgeId] = useState(caregiver.badge_id || "");
  const [employeeId, setEmployeeId] = useState(caregiver.employee_id || "");
  const [status, setStatus] = useState(caregiver.status || "ACTIVE");
  const [isAvailable, setIsAvailable] = useState(caregiver.is_available !== undefined ? caregiver.is_available : true);

        // Sync isAvailable with status changes
  useEffect(() => {
    // When status is ACTIVE, set available to true
    // When status is INACTIVE or ON_LEAVE, set available to false
    if (status === "ACTIVE") {
      setIsAvailable(true);
    } else if (status === "INACTIVE" || status === "ON_LEAVE") {
      setIsAvailable(false);
    }
  }, [status]);

    // Submission State
  /** @type {[boolean, Function]} Whether form is currently being submitted */
  const [submitting, setSubmitting] = useState(false);
  
  /** @type {[string|null, Function]} Error message to display, if any */
  const [error, setError] = useState(null);
  
    // Certification State
  /** @type {[Array, Function]} List of certifications */
  const [certifications, setCertifications] = useState([]);
  
  /** @type {[Array, Function]} List of certification types that were deleted */
  const [deletedCertTypes, setDeletedCertTypes] = useState([]);
  
  /** @type {[boolean, Function]} Whether to show add certification form */
  const [showAddCert, setShowAddCert] = useState(false);
  
  /** @type {[Object, Function]} New certification being added */
  const [newCert, setNewCert] = useState({
    cert_type: "",
    cert_name: "",
    cert_number: "",
    issuing_body: "",
    issued_date: "",
    expiry_date: "",
  });
  
    /** @type {[boolean, Function]} Whether certifications are loading */
  const [loadingCerts, setLoadingCerts] = useState(true);
  
  /** @type {[number|null, Function]} Index of certification being edited */
  const [editingCertIndex, setEditingCertIndex] = useState(null);
  
  /** @type {[Object, Function]} Certification being edited */
  const [editingCert, setEditingCert] = useState(null);

  /* ──────────────────────── Certification Management ──────────────────────── */
  
  /**
   * Add a new certification to the list
   */
  const handleAddCertification = () => {
    // Validate required fields
    if (!newCert.cert_type || !newCert.expiry_date) {
      setError("Certification type and expiry date are required");
      return;
    }
    
    // Add to list
    setCertifications([...certifications, { ...newCert, isNew: true }]);
    
    // Reset form
    setNewCert({
      cert_type: "",
      cert_name: "",
      cert_number: "",
      issuing_body: "",
      issued_date: "",
      expiry_date: "",
    });
    setShowAddCert(false);
    setError(null);
  };
  
    /**
     * Remove a certification from the list
     */
    const handleRemoveCertification = (index) => {
      const cert = certifications[index];
    
      // If this is an existing certification (not newly added), track it for deletion
      if (!cert.isNew && cert.cert_type) {
        setDeletedCertTypes([...deletedCertTypes, cert.cert_type]);
      }
    
      // Remove from the list
      setCertifications(certifications.filter((_, i) => i !== index));
    
      // If we're editing this cert, cancel the edit
      if (editingCertIndex === index) {
        setEditingCertIndex(null);
        setEditingCert(null);
      }
    };
  
  /**
   * Start editing a certification
   */
  const handleEditCertification = (index) => {
    setEditingCertIndex(index);
    setEditingCert({ ...certifications[index] });
    setShowAddCert(false); // Close add form if open
  };
  
  /**
   * Save edited certification
   */
  const handleSaveEditedCertification = () => {
    if (!editingCert.cert_type || !editingCert.expiry_date) {
      setError("Certification type and expiry date are required");
      return;
    }
    
    const updatedCerts = [...certifications];
    updatedCerts[editingCertIndex] = { ...editingCert, isModified: true };
    setCertifications(updatedCerts);
    
    setEditingCertIndex(null);
    setEditingCert(null);
    setError(null);
  };
  
  /**
   * Cancel editing a certification
   */
  const handleCancelEditCertification = () => {
    setEditingCertIndex(null);
    setEditingCert(null);
  };
  
  /* ──────────────────────── Form Validation ──────────────────────── */
  
  /**
   * Validates all form fields before submission.
   * Checks for required fields, valid data formats, and email format.
   * 
   * @returns {string|null} Error message if validation fails, null if valid
   */
  const validateForm = () => {
    if (!formFirstName.trim()) return "First name is required";
    if (!formLastName.trim()) return "Last name is required";
    if (!email.trim()) return "Email is required";
    if (!role) return "Role is required";
    if (!primaryShift) return "Primary shift is required";

        // Email format validation using regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Valid email is required";

    return null;
  };

  /* ──────────────────────── Form Submission ──────────────────────── */
  
  /**
   * Handles form submission.
   * 
   * Process:
   * 1. Validates form data
   * 2. Constructs update payload
   * 3. Sends API request to update caregiver
   * 4. Notifies parent component of success
   * 5. Closes modal after brief delay
   * 6. Handles and displays any errors
   * 
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
    const handleSubmit = async () => {
      const validationError = validateForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        // Construct the update payload with trimmed and formatted values
                const updatedData = {
          first_name: formFirstName.trim(),
          last_name: formLastName.trim(),
          email: email.trim(),
          phone: phone.trim() || "N/A", // Default to "N/A" if phone is empty
          role: role,
          primary_shift: primaryShift,
          badge_id: badgeId.trim() || "",
          employee_id: employeeId.trim() || "",
          status: status,
          is_available: isAvailable,
        };

        // Send update request to API
        await apiService.updateCaregiver(caregiver.caregiver_id, updatedData);

        // Update certifications - only send new or modified ones
        const newCertifications = certifications.filter(cert => cert.isNew);
        const modifiedCertifications = certifications.filter(cert => cert.isModified);

        // Add new certifications
        if (newCertifications.length > 0) {
          try {
            const certsToAdd = newCertifications.map(cert => {
              const { isNew, isModified, ...certData } = cert;
              return certData;
            });
            await apiService.addCaregiverCertifications(
              caregiver.caregiver_id,
              certsToAdd
            );
            console.log(`Added ${newCertifications.length} new certification(s)`);
          } catch (certError) {
            console.error('Failed to add new certifications:', certError);
            setError('Caregiver updated, but failed to add new certifications. Please try again.');
          }
        }

                // Update modified certifications
        if (modifiedCertifications.length > 0) {
          try {
            for (const cert of modifiedCertifications) {
              const { isNew, isModified, ...certData } = cert;
              await apiService.updateCaregiverCertification(
                caregiver.caregiver_id,
                cert.cert_type,
                certData
              );
            }
            console.log(`Updated ${modifiedCertifications.length} certification(s)`);
          } catch (certError) {
            console.error('Failed to update certifications:', certError);
            setError('Caregiver updated, but failed to update certifications. Please try again.');
          }
        }

        // Delete removed certifications
        if (deletedCertTypes.length > 0) {
          try {
            for (const certType of deletedCertTypes) {
              await apiService.deleteCaregiverCertification(
                caregiver.caregiver_id,
                certType
              );
            }
            console.log(`Deleted ${deletedCertTypes.length} certification(s)`);
          } catch (certError) {
            console.error('Failed to delete certifications:', certError);
            setError('Caregiver updated, but failed to delete certifications. Please try again.');
          }
        }

                // Notify parent component of successful update with merged data
        if (onUpdate) {
          onUpdate({
            ...caregiver,
            name: `${formFirstName} ${formLastName}`,
            email: email,
            phone: phone,
            role: role,
            shift: primaryShift,
            badge_id: badgeId,
            employee_id: employeeId,
            status: status,
            is_available: isAvailable,
          });
        }

        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 500);
      } catch (err) {
        console.error("Failed to update caregiver:", err);
        setError(err.message || "Failed to update caregiver. Please try again.");
      } finally {
        setSubmitting(false);
      }
    };

    /* ──────────────────────── Render Configuration ──────────────────────── */
  
  // Determine grid layout based on viewport size
  const gridCols = isMobile ? "1fr" : "1fr 1fr";

  /* ──────────────────────── Component Render ──────────────────────── */
  
  return (
    <>
            {/* Modal Backdrop - Semi-transparent overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 9998,
          animation: "fadeIn 0.2s ease",
        }}
      />

            {/* Modal Container - Main dialog box */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : "90%",
          maxWidth: 900,
          maxHeight: "90vh",
          background: CARD_BG,
          border: "2px solid #FFFFFF",
          borderRadius: 20,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 9999,
          animation: "slideIn 0.3s ease",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
                {/* ── Modal Header ── */}
        <div
          style={{
            padding: isMobile ? "20px" : "24px 32px",
            borderBottom: `2px solid ${BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
              color: W,
            }}
          >
            Edit Caregiver
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: W,
              cursor: "pointer",
              padding: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

                {/* ── Scrollable Content Area ── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "20px" : "28px 32px",
          }}
        >
                    {/* Error Banner - Displays validation or API errors */}
          {error && (
            <div
              style={{
                background: "rgba(255,107,107,0.12)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span style={{ fontSize: 14, color: "#FF6B6B", fontWeight: 500 }}>
                {error}
              </span>
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  border: "none",
                  color: "#FF6B6B",
                  cursor: "pointer",
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
          )}

                    {/* ── Form Content ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                        {/* Personal Information Section */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
                </svg>
                Personal Information
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gap: 16,
                }}
              >
                <FormField label="First Name" required isMobile={isMobile}>
                  <TextInput
                    value={formFirstName}
                    onChange={setFormFirstName}
                    placeholder="e.g. Sarah"
                  />
                </FormField>
                <FormField label="Last Name" required isMobile={isMobile}>
                  <TextInput
                    value={formLastName}
                    onChange={setFormLastName}
                    placeholder="e.g. Johnson"
                  />
                </FormField>
                <FormField label="Email" required isMobile={isMobile}>
                  <TextInput
                    value={email}
                    onChange={setEmail}
                    placeholder="e.g. sarah.johnson@facility.com"
                    type="email"
                  />
                </FormField>
                <FormField label="Phone" isMobile={isMobile}>
                  <TextInput
                    value={phone}
                    onChange={setPhone}
                    placeholder="e.g. +1-555-0123"
                    type="tel"
                  />
                </FormField>
              </div>
            </div>

                        {/* Professional Details Section */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2"
                >
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                </svg>
                Professional Details
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gap: 16,
                }}
              >
                <FormField label="Role" required isMobile={isMobile}>
                  <SelectInput
                    value={role}
                    onChange={setRole}
                    options={ROLE_OPTIONS}
                    placeholder="Select role"
                  />
                </FormField>
                <FormField label="Primary Shift" required isMobile={isMobile}>
                  <SelectInput
                    value={primaryShift}
                    onChange={setPrimaryShift}
                    options={SHIFT_OPTIONS}
                    placeholder="Select shift"
                  />
                </FormField>
                <FormField label="Badge ID" isMobile={isMobile}>
                  <TextInput
                    value={badgeId}
                    onChange={setBadgeId}
                    placeholder="Badge ID"
                  />
                </FormField>
                <FormField label="Employee ID" isMobile={isMobile}>
                  <TextInput
                    value={employeeId}
                    onChange={setEmployeeId}
                    placeholder="Employee ID"
                  />
                </FormField>
                                <FormField label="Status" isMobile={isMobile}>
                  <SelectInput
                    value={status}
                    onChange={setStatus}
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                  />
                </FormField>
              </div>
            </div>

            {/* Availability Section */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Availability
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Available for Assignment toggle */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    cursor: "pointer",
                    padding: "10px 16px",
                    background: isAvailable
                      ? "rgba(106,221,0,0.1)"
                      : INPUT_BG,
                    border: `1.5px solid ${
                      isAvailable
                        ? "rgba(106,221,0,0.4)"
                        : BORDER
                    }`,
                    borderRadius: 10,
                    transition: "all 0.2s",
                  }}
                >
                  <div
                    onClick={() => setIsAvailable(!isAvailable)}
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 12,
                      background: isAvailable ? "#6ADD00" : "rgba(255,255,255,0.2)",
                      position: "relative",
                      transition: "background 0.2s",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: W,
                        position: "absolute",
                        top: 3,
                        left: isAvailable ? 23 : 3,
                        transition: "left 0.2s",
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                    Available for New Assignments
                  </span>
                </label>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.6)",
                    fontStyle: "italic",
                    paddingLeft: 16,
                  }}
                >
                  {status === "ACTIVE" 
                    ? "Active caregivers are automatically available for assignments"
                    : "Inactive or on-leave caregivers are not available for assignments"}
                </div>
              </div>
            </div>
            
            {/* Certifications Section */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={ACCENT}
                  strokeWidth="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
                Certifications & Licenses
              </h3>
              
              {loadingCerts ? (
                <div style={{ textAlign: "center", padding: "20px", color: "rgba(255,255,255,0.6)" }}>
                  Loading certifications...
                </div>
              ) : (
                <>
                                    {/* Existing Certifications List */}
                  {certifications.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                      {certifications.map((cert, index) => (
                        editingCertIndex === index ? (
                          /* Edit Mode */
                          <div
                            key={index}
                            style={{
                              background: "rgba(74,144,226,0.08)",
                              border: "1.5px solid rgba(74,144,226,0.4)",
                              borderRadius: 12,
                              padding: isMobile ? "16px" : "20px",
                            }}
                          >
                            <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12 }}>
                              <FormField label="Certification Type" required isMobile={isMobile}>
                                <SelectInput
                                  value={editingCert.cert_type}
                                  onChange={(val) => setEditingCert({ ...editingCert, cert_type: val })}
                                  options={CERT_TYPE_OPTIONS}
                                  placeholder="Select type"
                                />
                              </FormField>
                              <FormField label="Certification Name" isMobile={isMobile}>
                                <TextInput
                                  value={editingCert.cert_name || ""}
                                  onChange={(val) => setEditingCert({ ...editingCert, cert_name: val })}
                                  placeholder="e.g., Basic Life Support"
                                />
                              </FormField>
                              <FormField label="Certificate Number" isMobile={isMobile}>
                                <TextInput
                                  value={editingCert.cert_number || ""}
                                  onChange={(val) => setEditingCert({ ...editingCert, cert_number: val })}
                                  placeholder="e.g., BLS-2024-12345"
                                />
                              </FormField>
                              <FormField label="Issuing Authority" isMobile={isMobile}>
                                <TextInput
                                  value={editingCert.issuing_body || ""}
                                  onChange={(val) => setEditingCert({ ...editingCert, issuing_body: val })}
                                  placeholder="e.g., American Heart Association"
                                />
                              </FormField>
                              <FormField label="Issue Date" isMobile={isMobile}>
                                <TextInput
                                  value={editingCert.issued_date || ""}
                                  onChange={(val) => setEditingCert({ ...editingCert, issued_date: val })}
                                  type="date"
                                />
                              </FormField>
                              <FormField label="Expiry Date" required isMobile={isMobile}>
                                <TextInput
                                  value={editingCert.expiry_date || ""}
                                  onChange={(val) => setEditingCert({ ...editingCert, expiry_date: val })}
                                  type="date"
                                />
                              </FormField>
                            </div>
                            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                              <button
                                onClick={handleSaveEditedCertification}
                                style={{
                                  background: "linear-gradient(135deg, #6ADD00, #5BC700)",
                                  border: "none",
                                  borderRadius: 10,
                                  padding: "10px 20px",
                                  color: W,
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "translateY(-1px)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                }}
                              >
                                ✓ Save Changes
                              </button>
                              <button
                                onClick={handleCancelEditCertification}
                                style={{
                                  background: "rgba(255,255,255,0.08)",
                                  border: `1.5px solid ${BORDER}`,
                                  borderRadius: 10,
                                  padding: "10px 20px",
                                  color: W,
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div
                            key={index}
                            style={{
                              background: "rgba(255,255,255,0.08)",
                              border: `1.5px solid ${cert.isNew ? 'rgba(106,156,0,0.5)' : cert.isModified ? 'rgba(255,165,0,0.5)' : BORDER}`,
                              borderRadius: 12,
                              padding: "14px 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 15, fontWeight: 700, color: W, marginBottom: 4 }}>
                                {cert.cert_name || cert.cert_type}
                                {cert.isNew && (
                                  <span style={{
                                    marginLeft: 8,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#6ADD00",
                                    background: "rgba(106,156,0,0.2)",
                                    padding: "2px 8px",
                                    borderRadius: 6,
                                  }}>
                                    NEW
                                  </span>
                                )}
                                {cert.isModified && (
                                  <span style={{
                                    marginLeft: 8,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#FFA500",
                                    background: "rgba(255,165,0,0.2)",
                                    padding: "2px 8px",
                                    borderRadius: 6,
                                  }}>
                                    MODIFIED
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                                {cert.cert_number && `#${cert.cert_number} • `}
                                Expires: {cert.expiry_date || "N/A"}
                                {cert.issuing_body && ` • ${cert.issuing_body}`}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={() => handleEditCertification(index)}
                                style={{
                                  background: "rgba(74,144,226,0.15)",
                                  border: "1.5px solid rgba(74,144,226,0.4)",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  color: ACCENT,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(74,144,226,0.25)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(74,144,226,0.15)";
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleRemoveCertification(index)}
                                style={{
                                  background: "rgba(255,107,107,0.15)",
                                  border: "1.5px solid rgba(255,107,107,0.4)",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  color: "#FF6B6B",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(255,107,107,0.25)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(255,107,107,0.15)";
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  
                  {/* Add Certification Form */}
                  {showAddCert ? (
                    <div
                      style={{
                        background: "rgba(74,144,226,0.08)",
                        border: "1.5px solid rgba(74,144,226,0.3)",
                        borderRadius: 12,
                        padding: isMobile ? "16px" : "20px",
                      }}
                    >
                      <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12 }}>
                        <FormField label="Certification Type" required isMobile={isMobile}>
                          <SelectInput
                            value={newCert.cert_type}
                            onChange={(val) => setNewCert({ ...newCert, cert_type: val })}
                            options={CERT_TYPE_OPTIONS}
                            placeholder="Select type"
                          />
                        </FormField>
                        <FormField label="Certification Name" isMobile={isMobile}>
                          <TextInput
                            value={newCert.cert_name}
                            onChange={(val) => setNewCert({ ...newCert, cert_name: val })}
                            placeholder="e.g., Basic Life Support"
                          />
                        </FormField>
                        <FormField label="Certificate Number" isMobile={isMobile}>
                          <TextInput
                            value={newCert.cert_number}
                            onChange={(val) => setNewCert({ ...newCert, cert_number: val })}
                            placeholder="e.g., BLS-2024-12345"
                          />
                        </FormField>
                        <FormField label="Issuing Authority" isMobile={isMobile}>
                          <TextInput
                            value={newCert.issuing_body}
                            onChange={(val) => setNewCert({ ...newCert, issuing_body: val })}
                            placeholder="e.g., American Heart Association"
                          />
                        </FormField>
                        <FormField label="Issue Date" isMobile={isMobile}>
                          <TextInput
                            value={newCert.issued_date}
                            onChange={(val) => setNewCert({ ...newCert, issued_date: val })}
                            type="date"
                          />
                        </FormField>
                        <FormField label="Expiry Date" required isMobile={isMobile}>
                          <TextInput
                            value={newCert.expiry_date}
                            onChange={(val) => setNewCert({ ...newCert, expiry_date: val })}
                            type="date"
                          />
                        </FormField>
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                        <button
                          onClick={handleAddCertification}
                          style={{
                            background: "linear-gradient(135deg, #6ADD00, #5BC700)",
                            border: "none",
                            borderRadius: 10,
                            padding: "10px 20px",
                            color: W,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          ✓ Add Certification
                        </button>
                        <button
                          onClick={() => {
                            setShowAddCert(false);
                            setNewCert({
                              cert_type: "",
                              cert_name: "",
                              cert_number: "",
                              issuing_body: "",
                              issued_date: "",
                              expiry_date: "",
                            });
                          }}
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            border: `1.5px solid ${BORDER}`,
                            borderRadius: 10,
                            padding: "10px 20px",
                            color: W,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddCert(true)}
                      style={{
                        background: "rgba(74,144,226,0.15)",
                        border: "1.5px dashed rgba(74,144,226,0.5)",
                        borderRadius: 12,
                        padding: "14px 20px",
                        width: "100%",
                        color: ACCENT,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(74,144,226,0.25)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(74,144,226,0.15)";
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                      Add Certification
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

                {/* ── Footer Actions ── */}
        <div
          style={{
            padding: isMobile ? "16px 20px" : "20px 32px",
            borderTop: `2px solid ${BORDER}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: `1.5px solid ${BORDER}`,
              borderRadius: 12,
              padding: "12px 28px",
              fontSize: 15,
              fontWeight: 600,
              color: W,
              cursor: submitting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              opacity: submitting ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!submitting)
                e.currentTarget.style.background = "rgba(255,255,255,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: submitting
                ? "rgba(74,144,226,0.3)"
                : "linear-gradient(135deg, #4A90E2, #357ABD)",
              border: "none",
              borderRadius: 12,
              padding: "12px 32px",
              fontSize: 15,
              fontWeight: 700,
              color: submitting ? "rgba(255,255,255,0.6)" : W,
              cursor: submitting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow: submitting
                ? "none"
                : "0 4px 16px rgba(74,144,226,0.3)",
            }}
            onMouseEnter={(e) => {
              if (!submitting)
                e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {submitting ? (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={W}
                  strokeWidth="2"
                  style={{
                    animation: "spin 1s linear infinite",
                  }}
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Updating...
              </>
            ) : (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={W}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>


    </>
  );
}
